import {
  isPositionInPath,
  nearestSampleLinear,
  forwardV,
  backwardV,
  relativeDirection,
  isDirFront,
  isDirBack,
  isDirLeft,
  isDirRight,
  angleDiffToTarget,
  capValue,
  unwrapAngle,
} from "./utils.mjs";
import { spark } from "./sparks.mjs";
import { sampleGhostAt } from "./ghost.mjs";
import { TEAMS } from "./constants.mjs";
import { F1EngineSound } from "./engine.mjs";

const SPARK_MIN_INTERVAL = 5000;
const SPARK_MAX_INTERVAL = 10000;
const SPARK_MIN_DURATION = 2000;
const SPARK_MAX_DURATION = 4000;
const ROAD_FRICTION = 0.99;
const OFFROAD_FRICTION = 0.97;
const HALF_PI = Math.PI * 0.5;

export class Car {
  constructor($el, carId, colorIndex, isBot, engineAudioData) {
    this.$el = $el;
    this.carId = carId;
    this.maxColorIndex = TEAMS.length;
    this.colorIndex = colorIndex;
    this.isVisible = true;
    this.raceStartTime = -1;
    this.$wheelFL = $el.querySelector(".wheel-fl");
    this.$wheelFR = $el.querySelector(".wheel-fr");
    this.control = { isBot };
    this.setEngineSound(engineAudioData);
    this.reset();
  }

  setEngineSound(engineAudioData) {
    if (!this.engineSound) {
      this.engineSound = engineAudioData
        ? new F1EngineSound({
            audioData: engineAudioData,
            gearMaxSpeeds: [0, 50, 90, 130, 170, 200, 220, 240], // mph
          })
        : null;
    }
  }

  setVisible(value) {
    this.isVisible = value;
    this.$el.classList.toggle("hidden", !value);
  }

  setBot(value) {
    this.control.isBot = value;
    if (this.control.isBot) {
      this.engineSound.stop();
    }
  }

  setGhostData(ghostData) {
    this.ghostData = ghostData;
    this.$el.classList.toggle("ghost", Boolean(this.ghostData));
  }

  setColor(colorIndex) {
    this.colorIndex = colorIndex;
    this.updateCarColor();
  }

  choosePreviousCarColor() {
    this.colorIndex--;
    this.updateCarColor();
  }

  chooseNextCarColor() {
    this.colorIndex++;
    this.updateCarColor();
  }

  updateCarColor() {
    if (this.colorIndex < 0) {
      this.colorIndex = this.maxColorIndex;
    }
    this.colorIndex %= this.maxColorIndex;
    this.$el.style.backgroundPositionX = `${this.colorIndex * -74}px`;
  }

  setTrackRacinglineSegments(value) {
    this.trackRacinglineSegments = value;
  }

  setStartingGrid(startingGrid) {
    this.heading = startingGrid.angle;
    const { x, y } = backwardV(startingGrid, 4 * this.trackScale);
    this.x = x;
    this.y = y;
    this.prevX = x;
    this.prevY = y;
    this.prevHeading = this.heading;
    this.targetHeading = this.heading;
    // Set initial DOM position so car appears at grid before race starts
    this.$el.style.transform = `translate(${this.x}px, ${this.y}px) rotate(${this.heading}rad)`;
  }

  getMaxSpeed() {
    return this.control.speed < 0
      ? this.control.maxReverseSpeed
      : this.control.maxForwardSpeed;
  }

  getLapTime(index) {
    return this.lapEndTimes[index] - this.lapStartTimes[index];
  }

  getLapCount() {
    return this.lapEndTimes.length;
  }

  getBestLapTime() {
    const lapCount = this.getLapCount();
    let bestLapTime = -1;
    let lapTime;
    for (let i = 0; i < lapCount; i++) {
      lapTime = this.getLapTime(i);
      if (bestLapTime < 0 || lapTime < bestLapTime) {
        bestLapTime = lapTime;
      }
    }
    return bestLapTime;
  }

  processSteering() {
    const control = this.control;

    // Apply player steering input (bots handle their own in updateBotControl)
    if (!control.isBot) {
      if (control.isSteeringLeft) {
        control.steeringAcc -= control.steeringSpeed;
      } else if (control.isSteeringRight) {
        control.steeringAcc += control.steeringSpeed;
      }
    }

    control.steeringAcc = capValue(control.steeringAcc, control.maxSteeringAcc);

    const playerMaxSpeed = this.getMaxSpeed();
    control.steering +=
      control.steeringAcc *
      (0.1 + ((playerMaxSpeed - control.speed) / playerMaxSpeed) * 0.9);
    control.steering *= 0.9;
    control.steering = capValue(control.steering);
    this.targetHeading = control.steering * this.maxHeading;

    control.acceleration *= 0.9;
    control.acceleration = capValue(
      control.acceleration,
      control.maxAcceleration
    );
    control.speed += control.acceleration;

    // check if player is offroad and apply corresponding friction
    const isOffroad = !isPositionInPath(
      this.$trackRoad,
      this.$trackRoadTarmac,
      this.x - this.camera.x + this.camera.hvw,
      this.y - this.camera.y + this.camera.hvh
    );
    this.isOffroad = isOffroad;
    let friction = !isOffroad ? ROAD_FRICTION : OFFROAD_FRICTION;

    if (control.speed > 0) {
      control.speed *= friction;
    } else if (control.speed < 0) {
      control.speed *= friction;
    }

    control.speed = capValue(control.speed, playerMaxSpeed);

    this.heading += this.targetHeading * control.speed * 0.015;
    this.vx = Math.cos(this.heading - HALF_PI) * control.speed;
    this.vy = Math.sin(this.heading - HALF_PI) * control.speed;

    control.steeringAcc *= 0.9;
  }

  async reset() {
    const control = this.control;
    control.isAccelerate = false;
    control.isBreak = false;
    control.isSteeringLeft = false;
    control.isSteeringRight = false;
    control.speed = 0;
    control.acceleration = 0;
    control.accelerationSpeed = 0.025;
    control.maxAcceleration = 0.5;
    control.maxForwardSpeed = 25;
    control.maxReverseSpeed = 5;
    control.steering = 0;
    control.steeringAcc = 0;
    control.steeringSpeed = 0.006;
    control.maxSteeringAcc = 0.1;

    this.bestLapTime = -1;
    this.lap = 0;
    this.lapProgress = 0;
    this.currentTrackSegmentIndex = -1;
    this.racinglinePoint = null;
    this.lookaheadPoint = null;
    this.vx = 0;
    this.vy = 0;
    this.targetHeading = 0;
    this.heading = 0;
    this.maxHeading = Math.PI * 0.2;
    // Previous frame state for interpolation
    this.prevX = 0;
    this.prevY = 0;
    this.prevHeading = 0;
    this.sparkStartTime = Math.random() * SPARK_MAX_INTERVAL;
    this.lapStartTimes = [];
    this.lapEndTimes = [];
    this.sparkEndTime =
      this.sparkStartTime +
      SPARK_MIN_DURATION +
      Math.random() * (SPARK_MAX_DURATION - SPARK_MIN_DURATION);

    // Ghost replay state - ghosts start in bot phase, not replaying
    this.isGhostReplayActive = false;

    this.updateCarColor();
  }

  async startRace() {
    // All cars wait until they cross the finish line (lapStartTime = -1)
    // Ghosts will drive from grid to start line first, then begin replay
    this.lapStartTimes[this.lap] = -1;
    this.raceStartTime = 0;
    this.isGhostReplayActive = false;

    if (!this.control.isBot && !this.ghostData && this.engineSound) {
      await this.engineSound.start();
    }
  }

  updateBotControl(now) {
    if (now < this.raceStartTime) return;

    const control = this.control;
    const trackRacinglineSegments = this.trackRacinglineSegments;

    const { x, y } = forwardV(this, 10 * this.trackScale);
    const closestRacinglinePoint = nearestSampleLinear(
      trackRacinglineSegments,
      x,
      y
    );

    let dx, dy;

    // pursue segments
    const lookaheadConfidence = Math.round(
      (control.speed / control.maxForwardSpeed) * 4
    );
    const pursueCount = 2 + lookaheadConfidence;
    const pursuePointIndex =
      (closestRacinglinePoint.index + pursueCount) %
      trackRacinglineSegments.length;
    let pursuePoint = trackRacinglineSegments[pursuePointIndex];

    if (this.ghostData) {
      pursuePoint = sampleGhostAt(this.ghostData, 0);
    }

    dx = pursuePoint.x - x;
    dy = pursuePoint.y - y;
    const pursueRacinglinePoint = {
      index: pursuePointIndex,
      point: pursuePoint,
      dist: closestRacinglinePoint.dist,
    };

    // look ahead segments
    const lookaheadCount = 6 + lookaheadConfidence;
    const lookaheadPointIndex =
      (closestRacinglinePoint.index + lookaheadCount) %
      trackRacinglineSegments.length;
    const lookaheadPoint = trackRacinglineSegments[lookaheadPointIndex];
    dx = lookaheadPoint.x - x;
    dy = lookaheadPoint.y - y;
    const lookaheadRacinglinePoint = {
      index: lookaheadPointIndex,
      point: lookaheadPoint,
      dist: closestRacinglinePoint.dist,
    };

    if (
      !this.racinglinePoint ||
      this.racinglinePoint.index < pursueRacinglinePoint.index ||
      Math.abs(this.racinglinePoint.index - pursueRacinglinePoint.index) >
        trackRacinglineSegments.length * 0.5
    ) {
      this.racinglinePoint = pursueRacinglinePoint;
    }

    if (
      !this.lookaheadPoint ||
      this.lookaheadPoint.index < lookaheadRacinglinePoint.index ||
      Math.abs(this.lookaheadPoint.index - lookaheadRacinglinePoint.index) >
        trackRacinglineSegments.length * 0.5
    ) {
      this.lookaheadPoint = lookaheadRacinglinePoint;
    }

    const targetDir = relativeDirection(
      this.x,
      this.y,
      this.heading,
      this.racinglinePoint.point.x,
      this.racinglinePoint.point.y
    );
    const angleDiff = angleDiffToTarget(
      this.x,
      this.y,
      this.heading,
      this.lookaheadPoint.point.x,
      this.lookaheadPoint.point.y
    );

    if (isDirFront(targetDir)) {
      const sensitivity = HALF_PI * (0.5 + (1 - lookaheadConfidence) * 0.2);
      const idealSpeed =
        control.maxForwardSpeed *
        ((sensitivity - Math.min(sensitivity, Math.abs(angleDiff))) /
          sensitivity);
      if (control.speed <= idealSpeed * 2) {
        control.isAccelerate = true;
        control.isBreak = false;
      } else {
        control.isAccelerate = false;
        control.isBreak =
          Math.random() > 0.75 &&
          control.speed > control.maxForwardSpeed * (1 - lookaheadConfidence);
      }
    } else if (isDirBack(targetDir)) {
      control.isAccelerate = false;
      control.isBreak = true;
    }

    if (control.isAccelerate) {
      if (this.racinglinePoint.dist > Math.abs(control.steeringAcc) * 5000) {
        const steeringSensitivity =
          Math.min(100, this.racinglinePoint.dist) / 100;
        if (isDirLeft(targetDir)) {
          control.steeringAcc -= control.steeringSpeed * steeringSensitivity;
        }
        if (isDirRight(targetDir)) {
          control.steeringAcc += control.steeringSpeed * steeringSensitivity;
        }
      }
    } else {
      if (isDirLeft(targetDir)) {
        if (control.speed >= 0) {
          control.steeringAcc -= control.steeringSpeed;
        } else {
          control.steeringAcc += control.steeringSpeed;
        }
      }
      if (isDirRight(targetDir)) {
        if (control.speed >= 0) {
          control.steeringAcc += control.steeringSpeed;
        } else {
          control.steeringAcc -= control.steeringSpeed;
        }
      }
    }
  }

  updateGhost(now) {
    // Only replay when ghost replay is active
    if (!this.isGhostReplayActive) return false;

    const lapStartTime = this.lapStartTimes[0];
    if (lapStartTime < 0 || now < lapStartTime) return false;

    // const lapDuration = this.ghostData[this.ghostData.length - 1].t * 1000;
    // const lapEndTime = lapStartTime + lapDuration;

    // const ghostSample = sampleGhostAt(
    //   this.ghostData,
    //   Math.floor(
    //     calcGeometricRatio(lapStartTime, lapEndTime, now) * lapDuration
    //   )
    // );

    const ghostSample = sampleGhostAt(this.ghostData, now - lapStartTime);
    if (ghostSample.index >= this.ghostData.length - 1) {
      this.lapStartTimes[0] = now;
    }
    this.heading = unwrapAngle(this.heading, ghostSample.heading);
    this.x = ghostSample.x;
    this.y = ghostSample.y;
    this.$el.style.transform = `translate(${this.x}px, ${this.y}px) rotate(${this.heading}rad)`;
    return true;
  }

  /**
   * Physics tick - runs at fixed 60 Hz for frame-rate independent physics.
   * @param {number} now - Time in ms since race start (for lap timing and sparks)
   */
  physicsTick(now) {
    // Ghost cars skip physics only when actively replaying
    // During bot phase (driving from grid to start line), physics runs normally
    if (this.ghostData && this.isGhostReplayActive) {
      return;
    }

    // Save previous state for interpolation
    this.prevX = this.x;
    this.prevY = this.y;
    this.prevHeading = this.heading;

    if (this.control.isBot) this.updateBotControl(now);

    this.processSteering();

    const control = this.control;
    const trackRacinglineSegments = this.trackRacinglineSegments;

    if (control.isAccelerate) {
      control.acceleration += control.accelerationSpeed;
    }

    if (control.isBreak) {
      control.acceleration -= control.accelerationSpeed;
    }

    this.targetHeading = control.steering * this.maxHeading;
    this.x += this.vx;
    this.y += this.vy;

    // Spark emission timing
    if (this.sparkStartTime >= 0 && now > this.sparkStartTime) {
      if (Math.random() < 0.1) {
        const vlen = Math.hypot(this.vx, this.vy);
        const nvx = this.vx / vlen;
        const nvy = this.vy / vlen;
        if (!this.isOffroad && control.speed > control.maxForwardSpeed * 0.4) {
          spark(now, this.x, this.y, -nvx * 2, -nvy * 2);
        }
      }
    }

    if (this.sparkEndTime >= 0 && now > this.sparkEndTime) {
      this.sparkStartTime =
        now +
        SPARK_MIN_INTERVAL +
        Math.random() * (SPARK_MAX_INTERVAL - SPARK_MIN_INTERVAL);
      this.sparkEndTime =
        this.sparkStartTime +
        SPARK_MIN_DURATION +
        Math.random() * (SPARK_MAX_DURATION - SPARK_MIN_DURATION);
    }

    // Track racing line and lap progress
    const closestRacinglinePoint = nearestSampleLinear(
      trackRacinglineSegments,
      this.x,
      this.y
    );
    this.racinglinePoint = closestRacinglinePoint;

    if (this.currentTrackSegmentIndex !== closestRacinglinePoint.index) {
      this.currentTrackSegmentIndex = closestRacinglinePoint.index;
      this.lapProgress++;
      if (this.currentTrackSegmentIndex === this.finishLine) {
        // Ghost cars: when reaching finish line for first time, activate replay
        if (this.ghostData && !this.isGhostReplayActive) {
          this.isGhostReplayActive = true;
          this.lapStartTimes[0] = now;
          this.lapProgress = 0;
          return; // Exit physics - ghost replay takes over
        }

        if (this.lapProgress / trackRacinglineSegments.length > 0.8) {
          this.lapEndTimes[this.lap] = now;
          const lapTime =
            this.lapEndTimes[this.lap] - this.lapStartTimes[this.lap];
          if (this.bestLapTime === -1 || lapTime < this.bestLapTime) {
            this.bestLapTime = lapTime;
            if (this.onBestLapTime) {
              this.onBestLapTime(this);
            }
          }
          this.lap++;
          if (this.onLapComplete) {
            this.onLapComplete(this);
          }
        }
        this.lapProgress = 0;
        this.lapStartTimes[this.lap] = now;
      }
    }
  }

  /**
   * Render car with interpolated position for smooth visuals.
   * @param {number} alpha - Interpolation factor (0-1) between physics frames
   */
  render(alpha) {
    // Ghost cars set their own position in updateGhost, skip interpolation
    // But during bot phase (before replay is active), render with interpolation
    if (this.ghostData && this.isGhostReplayActive) {
      return;
    }

    // Interpolate between previous and current state
    const renderX = this.prevX + (this.x - this.prevX) * alpha;
    const renderY = this.prevY + (this.y - this.prevY) * alpha;
    const renderHeading =
      this.prevHeading + (this.heading - this.prevHeading) * alpha;

    this.$el.style.transform = `translate(${renderX}px, ${renderY}px) rotate(${renderHeading}rad)`;
    this.$wheelFL.style.transform = `rotate(${this.targetHeading}rad)`;
    this.$wheelFR.style.transform = `rotate(${this.targetHeading}rad)`;
  }

  /**
   * Update engine sound based on current speed.
   * Called from render loop (not physics) for smooth audio.
   */
  updateEngineSound() {
    if (!this.control.isBot && this.engineSound) {
      this.engineSound.setSpeedMph(this.control.speed * 10);
    }
  }
}
